import { MAX_SCHEMA_DEPTH } from "./config";

// --- SCHEMA ADAPTER FOR GOOGLE ---
// Converts standard lowercase JSON schemas into Gemini's uppercase/nullable
// OpenAPI format — the dialect of the `responseSchema` config field.
//
// NOT CURRENTLY WIRED UP, deliberately. Both Gemini call sites use
// `responseJsonSchema`, which takes *standard* JSON Schema, and they used to
// pass this adapter's output into it: the OpenAPI dialect in the JSON Schema
// field. Kept because it is the correct adapter should anything ever need
// `responseSchema`, and because re-wrapping the schema here is the one-line
// revert if `responseJsonSchema` turns out to want it after all. Delete it once
// a real Gemini generation has confirmed the standard schema is accepted.
//
// Recurses through `anyOf` as well as properties/items: the node schema below
// is a union (a Group shape or a leaf shape), and a branch left untranslated
// would keep its lowercase types.

const toGeminiSchema = (schema) => {
  if (!schema || typeof schema !== "object") return schema;

  const geminiSchema = { ...schema };

  // 1. Handle type conversions and nullability
  if (Array.isArray(schema.type)) {
    // Converts e.g., ["boolean", "null"] -> type: "BOOLEAN", nullable: true
    const actualType = schema.type.find((t) => t !== "null");
    geminiSchema.type = actualType ? actualType.toUpperCase() : "STRING";
    if (schema.type.includes("null")) {
      geminiSchema.nullable = true;
    }
  } else if (typeof schema.type === "string") {
    // Converts e.g., "object" -> "OBJECT"
    geminiSchema.type = schema.type.toUpperCase();
  }

  // 2. Recurse into object properties
  if (geminiSchema.properties) {
    const newProps = {};
    for (const [key, val] of Object.entries(geminiSchema.properties)) {
      newProps[key] = toGeminiSchema(val);
    }
    geminiSchema.properties = newProps;
  }

  // 3. Recurse into array items
  if (geminiSchema.items) {
    geminiSchema.items = toGeminiSchema(geminiSchema.items);
  }

  // 4. Recurse into union branches
  if (Array.isArray(geminiSchema.anyOf)) {
    geminiSchema.anyOf = geminiSchema.anyOf.map(toGeminiSchema);
  }

  return geminiSchema;
};

// --- DYNAMIC SCHEMA GENERATOR ---
//
// A node is one of two SHAPES, not one shape with optional fields:
//
//   Group — a container. Holds `children`, and its `value`/`unit` describe how
//           the children repeat (a rounds/sets count) or how long they run for
//           (a time cap).
//   Leaf  — a step. Work / Rest / Warmup / Cooldown. Carries the prescription
//           itself (reps, calories, a duration) and NO `children` key.
//
// Declaring them separately is the point. A single shape with `children` hanging
// off every node made a `Work` node holding three nested `Work` nodes a
// SCHEMA-VALID answer — which is exactly what one Gemini generation produced,
// silently discarding two of the three prescriptions at compile time (the
// compiler descends into anything with children but only emits leaves). The
// providers that enforce a schema (Gemini's `responseJsonSchema`, Anthropic's
// tool `input_schema`) can now refuse that shape outright instead of leaving it
// to `flattenLeafContainers` to repair downstream. The repair stays: the
// json_object-only providers (Ollama, OpenRouter, the OpenAI-compatible path)
// get no shape enforcement at all.
//
// Splitting the shapes also splits the unit enums, so `Group(unit="reps")` and
// `Work(unit="rounds")` stop being expressible.

// Fields carried by both shapes.
const IDENTITY_PROPERTIES = {
  id: { type: "string" },
  label: { type: "string" },
  value: { type: "number" },
};

// Leaf-only fields. `setScheme` belongs here rather than on the Group: the
// compiler resolves per-set values on leaves, so a scheme authored on a Group
// silently ran set 1's figures for every set (issue #74).
const LEAF_PROPERTIES = {
  ...IDENTITY_PROPERTIES,
  type: {
    type: "string",
    enum: ["Work", "Rest", "Warmup", "Cooldown"],
  },
  unit: {
    type: "string",
    enum: ["reps", "cal", "s", "m"],
  },
  autoStart: { type: "boolean" }, // Stick to strict boolean for Gemini
  // Required: an optional field is one strict-schema providers (Gemini) simply
  // leave out, prompt or no prompt — every generated main lift came back
  // without its effort cue until this was required. Rest steps use "".
  intensityTarget: {
    type: "string",
    description:
      "Effort cue for a Work step, e.g. 'RIR 2-3', 'RPE 8', 'All-out', 'Zone 2 - conversational'. Empty string for Rest.",
  },
  // Optional VBT load fields (Work only). Declared here so strict-schema
  // providers (Gemini) don't drop them — the prompt already instructs the
  // model to emit one on ramped/working sets.
  weight: { type: "number" },
  intensityPct: { type: "number" },
  // Optional per-set variation for a repeated phase (issue #29). Each present
  // dimension is an array with one entry per set (length === the enclosing
  // sets-Group's value). Only varying dimensions appear; uniform parameters
  // stay on the static field above, mirroring set 1. See docs/schema.md.
  setScheme: {
    type: "object",
    properties: {
      value: { type: "array", items: { type: "number" } },
      weight: { type: "array", items: { type: "number" } },
      intensityPct: { type: "array", items: { type: "number" } },
    },
  },
};

const LEAF_SCHEMA = {
  type: "object",
  properties: LEAF_PROPERTIES,
  required: ["id", "label", "type", "value", "unit", "intensityTarget"],
};

// A container. `children` is REQUIRED — an empty Group is a legitimate thing to
// create in the editor but never a thing to generate, and making it required is
// what stops "Group" being chosen as a cheap way to avoid prescribing anything.
const buildGroupSchema = (currentDepth, maxDepth) => ({
  type: "object",
  properties: {
    ...IDENTITY_PROPERTIES,
    type: {
      type: "string",
      enum: ["Group"],
    },
    unit: {
      type: "string",
      enum: ["rounds", "sets", "workout", "s", "m"],
    },
    children: {
      type: "array",
      items: buildNodeSchema(currentDepth + 1, maxDepth),
    },
  },
  required: ["id", "label", "type", "value", "unit", "children"],
});

// Either shape. At the depth limit only a leaf remains — a Group there could
// hold nothing, so offering it would just invite a childless container.
function buildNodeSchema(currentDepth, maxDepth) {
  if (currentDepth >= maxDepth) return LEAF_SCHEMA;
  return {
    anyOf: [buildGroupSchema(currentDepth, maxDepth), LEAF_SCHEMA],
  };
}

// The root is always the session container: Group(id="root", unit="workout",
// value=1). Typing it as a Group rather than a node union means a plan can
// never come back as a bare exercise with no session around it.
const WORKOUT_PLAN_SCHEMA = {
  type: "object",
  properties: {
    workout_plan: buildGroupSchema(0, MAX_SCHEMA_DEPTH),
  },
  required: ["workout_plan"],
};

// A described workout (#77) comes back as the plan PLUS a `meta` object — when,
// how long, what it was called. The providers that enforce a schema (Gemini,
// MiniMax) used to be handed WORKOUT_PLAN_SCHEMA here too, which has no `meta`,
// so the model obediently left it out and every described workout arrived with
// no duration, no date and no title. The duration is required so the model
// always commits to a figure — the one the user stated, or its own estimate.
const LOG_WORKOUT_SCHEMA = {
  type: "object",
  properties: {
    workout_plan: WORKOUT_PLAN_SCHEMA.properties.workout_plan,
    meta: {
      type: "object",
      properties: {
        title: { type: "string" },
        activityType: {
          type: "string",
          enum: [
            "strength",
            "run",
            "ride",
            "row",
            "swim",
            "walk",
            "class",
            "other",
          ],
        },
        durationMinutes: { type: "integer" },
        durationEstimated: { type: "boolean" },
        daysAgo: { type: "integer" },
        rating: { type: "integer" },
        notes: { type: "string" },
      },
      required: [
        "title",
        "activityType",
        "durationMinutes",
        "durationEstimated",
        "daysAgo",
      ],
    },
  },
  required: ["workout_plan", "meta"],
};

// --- ENHANCE EXERCISE JSON SCHEMA (STANDARD) ---
const ENHANCE_EXERCISE_SCHEMA = {
  type: "object",
  properties: {
    description: {
      type: "string",
      description:
        "A concise 1-2 sentence description of the exercise and its benefits.",
    },
    formTips: {
      type: "array",
      items: { type: "string" },
      description:
        "A list of 3-5 actionable tips for maintaining perfect form.",
    },
    commonMistakes: {
      type: "array",
      items: { type: "string" },
      description:
        "A list of 2-3 common mistakes to avoid during this exercise.",
    },
    alternatives: {
      type: "array",
      items: { type: "string" },
      description:
        "A list of 2-3 alternative exercises that target the same muscle groups.",
    },
  },
  required: ["description", "formTips", "commonMistakes", "alternatives"],
};

export {
  toGeminiSchema,
  WORKOUT_PLAN_SCHEMA,
  LOG_WORKOUT_SCHEMA,
  ENHANCE_EXERCISE_SCHEMA,
};
